import React, { useEffect, useState } from 'react';
import { 
    ArrowPathIcon, 
    CheckIcon, 
    ExclamationTriangleIcon,
    UserGroupIcon 
} from '@heroicons/react/24/outline';
import Helper from "../helpers";
import config from "../config";

const baseUrl = config.apiBaseUrl;

const GoCardlessUserImport = () => {
    const [loading, setLoading] = useState(true);
    const [gcUsers, setGcUsers] = useState([]);
    const [venueUsers, setVenueUsers] = useState([]);
    const [userMatches, setUserMatches] = useState({});
    const [venueKey, setCurrentVenueKey] = useState("");
    const [venueKeyOptions, setCurrentVenueKeyOptions] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            const token = await Helper.getCookie('wh-jwt');
            if(!venueKey) return;
            const gcResponse = await fetch(`${baseUrl}/api/paymentadmin/importusers/${venueKey}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const gcData = await gcResponse.json();
            setGcUsers(gcData);

            const venueResponse = await fetch(`${baseUrl}/api/users/venueusers/${venueKey}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const venueData = await venueResponse.json();
            setVenueUsers(venueData);

            setLoading(false);
        };

        Helper.getAdminVenues(setCurrentVenueKey, setCurrentVenueKeyOptions)
            .then(fetchUsers);
    }, [venueKey]);

    const handleUserMatchChange = (gcUserId, venueUserId) => {
        setUserMatches({
            ...userMatches,
            [gcUserId]: venueUserId
        });
    };

    const handleSave = async (gcUserId) => {
        const user = gcUsers.find(u => u.id === gcUserId);
        const venueUser = venueUsers.find(u => u.email === userMatches[gcUserId]);

        if (!user || !venueUser) {
            console.error('User or Venue User not found');
            return;
        }

        const payload = {
            ExternalSubsciprtionName: user.subscriptions[0].plan,
            ExternalSubsciprtionId: user.subscriptions[0].id,
            ExternalSubsciprtionIdUserId: gcUserId,
            UserId: venueUser.userId
        };

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/subscription/ImportUserWithSubscription/${venueKey}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                console.log('User and subscription saved successfully');
            } else {
                console.error('Failed to save user and subscription');
            }
        } catch (error) {
            console.error('Error saving user and subscription:', error);
        }
    };

    const handleSaveAll = () => {
        // Implement save logic for all users
        console.log('Saving all matches');
    };

    return (
        <div className="space-y-6">
            {/* Hidden venue selector */}
            {venueKeyOptions.length > 0 && (
                <select
                    className="hidden"
                    value={venueKey}
                    onChange={(e) => setCurrentVenueKey(e.target.value)}
                >
                    {venueKeyOptions.map((venueKey) => (
                        <option key={venueKey} value={venueKey}>
                            {venueKey}
                        </option>
                    ))}
                </select>
            )}

            {loading ? (
                <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-12 text-center">
                    <ArrowPathIcon className="h-8 w-8 text-emerald-400 mx-auto mb-4 animate-spin" />
                    <h3 className="text-lg font-semibold text-white mb-2">Loading GoCardless Users</h3>
                    <p className="text-gray-400">Fetching user data and subscriptions...</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Header with Save All Button */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <UserGroupIcon className="h-6 w-6 text-emerald-400" />
                            <div>
                                <h2 className="text-xl font-semibold text-white">User Import</h2>
                                <p className="text-gray-400 text-sm">Match GoCardless customers to venue users</p>
                            </div>
                        </div>
                        <button
                            onClick={handleSaveAll}
                            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                        >
                            <CheckIcon className="h-4 w-4" />
                            <span>Save All</span>
                        </button>
                    </div>

                    {/* Users Table */}
                    <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-800/50 border-b border-gray-700/30">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Email</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Subscription</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Start Date</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Amount</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Assign to User</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700/30">
                                    {gcUsers.map(gcUser => (
                                        gcUser.subscriptions.map(subscription => (
                                            <tr key={subscription.id} className="hover:bg-gray-800/30 transition-colors duration-200">
                                                <td className="px-6 py-4 text-sm text-white">{gcUser.email}</td>
                                                <td className="px-6 py-4 text-sm text-gray-300">{subscription.plan} ({subscription.id})</td>
                                                <td className="px-6 py-4 text-sm text-gray-300">{subscription.startDate}</td>
                                                <td className="px-6 py-4 text-sm text-gray-300">{subscription.amount}</td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        subscription.status === 'active' 
                                                            ? 'bg-emerald-900/20 text-emerald-400 border border-emerald-800/30'
                                                            : 'bg-yellow-900/20 text-yellow-400 border border-yellow-800/30'
                                                    }`}>
                                                        {subscription.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <select
                                                        value={userMatches[gcUser.id] || ''}
                                                        onChange={(e) => handleUserMatchChange(gcUser.id, e.target.value)}
                                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                                                    >
                                                        <option value="" disabled>Please select</option>
                                                        {venueUsers.map(venueUser => (
                                                            <option key={venueUser.id} value={venueUser.email}>
                                                                {venueUser.email}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => handleSave(gcUser.id)}
                                                        disabled={!userMatches[gcUser.id]}
                                                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
                                                    >
                                                        <CheckIcon className="h-3 w-3" />
                                                        <span>Save</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {gcUsers.length === 0 && (
                            <div className="p-12 text-center">
                                <ExclamationTriangleIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-white mb-2">No Users Found</h3>
                                <p className="text-gray-400">No GoCardless users were found for this venue.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoCardlessUserImport;
